import type {
  ArchiveEvent,
  ArchiveCategory,
  ArchiveEra,
  ArchiveFaq,
  ArchiveVoice,
} from "@/types/archive";

export const siteInfo = {
  name: "暗线档案",
  tagline: "按时间线梳理的暗黑史与未解之谜",
  description:
    "暗线档案以时间轴为核心，系统梳理历史灾难、未解之谜、文明兴衰与重大技术事故，用清晰的时间顺序串联碎片化信息，还原暗黑脉络与事件演变全程。",
  email: "contact@anxian-archive.com",
  est: "2019",
};

export const categories: ArchiveCategory[] = [
  {
    id: "disaster",
    name: "历史灾难",
    desc: "瘟疫、崩盘、自然灾害，记录人类在极端冲击下的应对与代价。",
    icon: "ri-fire-line",
  },
  {
    id: "mystery",
    name: "未解之谜",
    desc: "悬而未决的失踪、爆炸与遗迹，把已知证据与开放疑问并排放置。",
    icon: "ri-question-line",
  },
  {
    id: "civilization",
    name: "文明兴衰",
    desc: "从鼎盛到沉寂，追踪城市、典籍与制度缓慢退场的过程。",
    icon: "ri-ancient-gate-line",
  },
  {
    id: "tech",
    name: "科技事故",
    desc: "工程与技术的失控瞬间，以及它们如何改写行业安全标准。",
    icon: "ri-radar-line",
  },
];

export const eras: ArchiveEra[] = [
  { id: "all", name: "全部纪元", short: "全部" },
  { id: "ancient", name: "古代 · 公元前至公元500", short: "古代" },
  { id: "medieval", name: "中世纪 · 500 至 1500", short: "中世纪" },
  { id: "early", name: "近代 · 1500 至 1900", short: "近代" },
  { id: "modern", name: "现代 · 1900 至 2000", short: "现代" },
  { id: "contemporary", name: "当代 · 2000 至今", short: "当代" },
];

export const events: ArchiveEvent[] = [
  {
    id: "alexandria-library",
    title: "亚历山大图书馆的湮灭",
    yearLabel: "约公元前 48 年",
    year: -48,
    era: "ancient",
    category: "civilization",
    location: "埃及 · 亚历山大港",
    readTime: "9 分钟",
    featured: true,
    tags: ["典籍散佚", "地中海文明", "知识断层"],
    summary:
      "古代世界规模最大的知识宝库，在战火与长期衰败中逐渐散佚。数十万卷典籍的消失，成为人类集体记忆上一次难以复原的断层。",
    cover:
      "https://readdy.ai/api/search-image?query=Ancient%20grand%20library%20hall%20at%20dusk%20with%20towering%20stone%20columns%20and%20rows%20of%20scroll%20shelves%2C%20dim%20amber%20torchlight%2C%20deep%20shadows%2C%20cinematic%20melancholic%20mood%2C%20desaturated%20warm%20palette%2C%20atmospheric%20dust%20in%20the%20air&width=1200&height=800&seq=anxian-alexandria&orientation=landscape",
    lead:
      "在托勒密王朝的扶持下，亚历山大图书馆几乎网罗了当时地中海世界能够收集到的全部文本。它不只是藏书楼，更是一座把翻译、抄写、校勘和天文学研究集中在一起的机构。",
    body: [
      "鼎盛时期，学者在这里完成了《七十士译本》的翻译工作，也建立起对希腊史诗与戏剧的早期校勘体系。据古代文献的夸张记载，馆藏卷轴数量达到数十万之巨，具体数字已无法核实，但它在当时是唯一具备这种规模的学术中心。",
      "图书馆的衰落并非某一夜之间的火灾。凯撒在内战期间点燃港口船只，火势有可能波及临海的书库，这是流传最广的一次毁灭性事件；此后数百年间，经费削减、学者流失、罗马政局动荡与藏书维护的困难，共同推动了它的解体。",
      "真正难以估量的是被带走的可能性。许多只在此处留存副本的著作，随着卷轴的腐坏和抄写体系的中断而彻底消失。今天我们读到的古代世界，在很大程度上是被这场漫长的散佚筛选过之后的结果。",
    ],
    timeline: [
      {
        date: "约公元前 285 年",
        title: "建馆与聚书",
        desc: "托勒密一世启动图书馆建设，通过购置与抄录两种方式系统性搜集地中海文本。",
      },
      {
        date: "公元前 3 世纪",
        title: "学术黄金期",
        desc: "馆内学者完成史诗校勘与大规模翻译，图书馆成为希腊化世界的学术中枢。",
      },
      {
        date: "公元前 48 年",
        title: "港口大火",
        desc: "凯撒与政敌交战时引燃港内船只，火势可能蔓延至临海书库，藏书遭受重创。",
      },
      {
        date: "公元 3 世纪末",
        title: "机构衰败",
        desc: "罗马统治下的经费与人事长期失控，图书馆分批迁移、散落，逐渐名存实亡。",
      },
      {
        date: "公元 7 世纪",
        title: "最后的记载",
        desc: "关于残余藏书的记载彻底中断，古代文本的大规模存续时代随之结束。",
      },
    ],
  },
  {
    id: "pompeii-79",
    title: "庞贝末日：维苏威火山下的时间胶囊",
    yearLabel: "公元 79 年",
    year: 79,
    era: "ancient",
    category: "disaster",
    location: "意大利 · 那不勒斯湾",
    readTime: "8 分钟",
    featured: true,
    tags: ["火山喷发", "考古", "罗马帝国"],
    summary:
      "维苏威火山的喷发在十几个小时内掩埋了庞贝与赫库兰尼姆，也把罗马帝国的日常完整封存进火山灰里。",
    cover:
      "https://readdy.ai/api/search-image?query=Roman%20city%20ruins%20under%20a%20dark%20ash%20filled%20sky%2C%20volcanic%20eruption%20plume%20glowing%20orange%20on%20the%20horizon%2C%20silhouettes%20of%20broken%20columns%20and%20stone%20streets%2C%20cinematic%20ominous%20atmosphere%2C%20muted%20charcoal%20and%20ember%20tones&width=1200&height=800&seq=anxian-pompeii&orientation=landscape",
    lead:
      "公元79年秋，维苏威火山结束了数百年的沉寂。当时的居民已经习惯了这座山的温和，山脚的葡萄园与海水浴场让庞贝成为帝国富裕阶层喜爱的度假地。",
    body: [
      "喷发进入第二天凌晨后，数轮火山碎屑流以极高速度沿山坡倾泻，温度与冲击力足以在瞬间致命。庞贝被数米厚的浮石与灰烬覆盖，赫库兰尼姆则被更密集的泥流掩埋，保存状态也因此差异明显。",
      "十八世纪开始的系统发掘，让面包房、酒吧、涂鸦与壁画一同重见天日。火山灰形成的密闭环境抑制了有机物腐坏，考古学家得以复原出一座罗马城市在灾难前一天的生活切片。",
      "今天回看这段时间线，最令人震动的是预警的缺失：当时并没有现代意义上的火山学，居民对山体活动的判断依赖经验，而经验这一次给了完全错误的答案。",
    ],
    timeline: [
      {
        date: "公元 79 年 10 月 24 日",
        title: "喷发开始",
        desc: "火山口炸开，浮石与火山灰被喷射到平流层，天空迅速变暗。",
      },
      {
        date: "当日下午",
        title: "落石阶段",
        desc: "庞贝城内不断堆积浮石，屋顶陆续坍塌，居民开始向港口与乡间撤离。",
      },
      {
        date: "次日凌晨",
        title: "碎屑流抵达",
        desc: "高温火山碎屑流沿山坡快速下泄，抵达庞贝与赫库兰尼姆，造成即时致命。",
      },
      {
        date: "喷发后数日",
        title: "全城掩埋",
        desc: "数米厚的火山物质把两座城市完整覆盖，位置逐渐从记忆中消失。",
      },
      {
        date: "1748 年",
        title: "重新被发现",
        desc: "系统性发掘启动，庞贝成为世界上保存最完整的古罗马城市遗址。",
      },
    ],
  },
  {
    id: "maya-collapse",
    title: "玛雅低地的无声退场",
    yearLabel: "约公元 900 年",
    year: 900,
    era: "medieval",
    category: "civilization",
    location: "中美洲 · 尤卡坦半岛",
    readTime: "10 分钟",
    featured: true,
    tags: ["古典期终结", "考古学", "气候记录"],
    summary:
      "曾经建有金字塔与天文台的玛雅城邦，在数十年间陆续被遗弃，原因至今仍是考古学最著名的问题之一。",
    cover:
      "https://readdy.ai/api/search-image?query=Overgrown%20ancient%20Maya%20stone%20pyramid%20swallowed%20by%20dense%20jungle%20vines%20at%20twilight%2C%20mist%20between%20trees%2C%20moody%20green%20and%20deep%20umber%20tones%2C%20cinematic%20archaeological%20documentary%20feeling%2C%20low%20contrast%20muted%20light&width=1200&height=800&seq=anxian-maya&orientation=landscape",
    lead:
      "古典期的玛雅并不是一个统一帝国，而是数十个彼此竞争又互相联姻的城邦。它们共享文字、历法与宗教体系，也共享同一套脆弱的农业基础。",
    body: [
      "蒂卡尔、卡拉克穆尔与帕伦克留下了高耸的金字塔与精确记录天象的石碑。这些工程需要庞大的劳动力与稳定的补给，而补给完全依赖雨水与人工蓄水系统，这让整个体系对气候波动异常敏感。",
      "近年通过湖泊沉积物和石笋重建的古气候数据显示，公元九世纪至十世纪之间出现了多轮严重干旱，其中若干次持续数十年。干旱叠加土壤退化、贸易网络断裂与城邦间战事，最终让南部低地的政治中心无法维持。",
      "值得注意的是，玛雅文明并未彻底消失。人口向北部尤卡坦地区与高地迁移，新的城邦延续了数百年。所谓“神秘消失”，更准确的表述是一次政治与人口中心的转移。",
    ],
    timeline: [
      {
        date: "约公元 250 至 600 年",
        title: "古典期扩张",
        desc: "南部低地城邦大规模营建，文字与历法体系成熟，人口达到高峰。",
      },
      {
        date: "约公元 800 年",
        title: "干旱与压力",
        desc: "多轮持续干旱出现，蓄水系统承压，城邦间的资源争夺趋于激烈。",
      },
      {
        date: "约公元 850 至 900 年",
        title: "南部遗弃",
        desc: "多座核心城邦停止营建石碑，王权象征中断，人口逐渐外迁。",
      },
      {
        date: "约公元 1000 年",
        title: "重心北移",
        desc: "尤卡坦北部的奇琴伊察等城邦崛起，成为新的政治与贸易中心。",
      },
      {
        date: "16 世纪",
        title: "接触与断裂",
        desc: "西班牙殖民者的到来打断了仍在延续的玛雅政治实体，石碑时代彻底结束。",
      },
    ],
  },
  {
    id: "easter-island-moai",
    title: "复活节岛石像的沉默",
    yearLabel: "约公元 1200 年",
    year: 1200,
    era: "medieval",
    category: "mystery",
    location: "智利 · 复活节岛",
    readTime: "9 分钟",
    tags: ["巨石文化", "生态史", "拉帕努伊"],
    summary:
      "近千尊巨型石像被立起，又在资源枯竭中停下。岛屿社会从繁盛走向崩塌的过程，留下诸多难以证实的猜想。",
    cover:
      "https://readdy.ai/api/search-image?query=Row%20of%20giant%20stone%20moai%20statues%20on%20a%20barren%20volcanic%20island%20cliff%20at%20dusk%2C%20low%20clouds%20and%20wind%20swept%20grass%2C%20desolate%20moody%20atmosphere%2C%20muted%20grey%20and%20bronze%20tones%2C%20cinematic%20wide%20shot&width=1200&height=800&seq=anxian-easter-island&orientation=landscape",
    lead:
      "拉帕努伊的居民在没有金属工具、没有轮车、没有大型牲畜的条件下，开采、搬运并竖立起数百尊重量以吨计的石像，这件事本身就已经足够说明他们的社会组织能力。",
    body: [
      "关于石像如何被移动，学界先后提出过绳索摇摆、木橇滑行与直立“行走”等多种方案，实验考古也验证了其中几种在物理上可行。分歧更多集中在社会成本：这些工程究竟是在怎样的资源条件下完成的。",
      "花粉与地层研究显示，岛屿在被人类定居后被大规模砍伐，原有的棕榈林逐渐消失。森林的消失意味着造船与搬运所需的木材同步枯竭，也意味着土壤保持能力下降，农业产出随之收缩。",
      "曾有观点认为岛上发生了剧烈的内战与人口崩溃，但近年的研究修正了这一叙事：崩溃更像是缓慢的资源收缩与制度调整，而非一夜之间的自我毁灭。石像的停工，是这个漫长过程最直观的标志。",
    ],
    timeline: [
      {
        date: "约公元 1200 年",
        title: "定居与营建",
        desc: "波利尼西亚航海者定居岛上，开始建造祭台并雕刻第一批石像。",
      },
      {
        date: "公元 1400 至 1600 年",
        title: "石像鼎盛",
        desc: "石像体量不断增大，采石场与运输路线形成规模化组织，森林被持续消耗。",
      },
      {
        date: "约公元 1600 年",
        title: "停工与转向",
        desc: "营建活动明显减少，宗教重心转向新的祭祀形式，社会结构开始重组。",
      },
      {
        date: "1722 年",
        title: "欧洲人抵达",
        desc: "荷兰探险者在复活节当天登陆，岛上人口已远低于历史峰值。",
      },
      {
        date: "19 世纪",
        title: "抄掠与疾病",
        desc: "外来者带走岛民、传入疫病，口述传统几近中断，早期历史只能依赖考古重建。",
      },
    ],
  },
  {
    id: "black-death",
    title: "黑死病：改变欧洲的黑夜",
    yearLabel: "1347 年",
    year: 1347,
    era: "medieval",
    category: "disaster",
    location: "欧洲",
    readTime: "11 分钟",
    featured: true,
    tags: ["鼠疫", "人口结构", "中世纪"],
    summary:
      "一场由鼠疫杆菌引发的瘟疫在五年间带走欧洲约三分之一人口，也重塑了此后数百年的社会结构与劳动力秩序。",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20medieval%20European%20street%20at%20night%20in%20thick%20fog%2C%20narrow%20timber%20houses%2C%20single%20lantern%20glow%2C%20abandoned%20market%20stalls%2C%20dark%20oppressive%20atmosphere%2C%20muted%20brown%20and%20ash%20grey%20palette%2C%20cinematic%20historical%20drama&width=1200&height=800&seq=anxian-blackdeath&orientation=landscape",
    lead:
      "1347 年，热那亚商船从黑海返航时把鼠疫杆菌带到了西西里。此后五年间，疫情沿贸易路线与港口网络迅速扩张，几乎没有地理阻隔能真正阻挡它。",
    body: [
      "当时的医学体系无法解释病因，应对方式从隔离、驱逐到大规模集会祈祷，效果各异。城市死亡率普遍偏高，部分城镇的死亡人数超过总人口的一半，乡村地区因人口分散而相对缓和。",
      "人口的骤降带来了矛盾的后果：短期内劳动力极度稀缺，幸存者的工资水平与议价能力显著上升，传统的农奴依附关系被削弱；长期看，土地配置、继承规则与宗教权威都因此发生松动。",
      "黑死病也不是一次性的灾难。此后数百年间，鼠疫在欧洲反复暴发，成为城市公共卫生制度、检疫体系与港口管控的直接推动力。",
    ],
    timeline: [
      {
        date: "1347 年 10 月",
        title: "登陆西西里",
        desc: "来自黑海的商船把病原带入墨西拿港，疫情在港口城市迅速扩散。",
      },
      {
        date: "1348 年",
        title: "蔓延西欧",
        desc: "沿地中海贸易线与陆路商道北上，意大利、法国、伊比利亚半岛相继暴发。",
      },
      {
        date: "1349 年",
        title: "抵达英伦与北欧",
        desc: "疫情跨过海峡并传入北欧港口，英格兰人口在一年多内锐减。",
      },
      {
        date: "1351 年",
        title: "第一波趋缓",
        desc: "首轮大流行逐渐平息，但地方性反复暴发持续了数百年。",
      },
      {
        date: "14 世纪末",
        title: "社会重组",
        desc: "劳动力稀缺推动工资上涨与土地关系变化，城市公共卫生制度开始成形。",
      },
    ],
  },
  {
    id: "mary-celeste",
    title: "玛丽·塞莱斯特号：空无一人的幽灵船",
    yearLabel: "1872 年",
    year: 1872,
    era: "early",
    category: "mystery",
    location: "大西洋 · 亚速尔群岛附近",
    readTime: "8 分钟",
    tags: ["航海悬案", "商船", "大西洋"],
    summary:
      "这艘商船被发现时货物完好、食物尚在、救生艇缺失，却不见一名船员，至今无人能给出被普遍接受的解释。",
    cover:
      "https://readdy.ai/api/search-image?query=Abandoned%20sailing%20ship%20drifting%20alone%20on%20a%20calm%20foggy%20open%20ocean%20at%20dawn%2C%20torn%20sails%20and%20weathered%20hull%2C%20cold%20grey%20mist%2C%20eerie%20silent%20atmosphere%2C%20muted%20slate%20and%20pale%20ochre%20tones%2C%20cinematic%20maritime%20mystery&width=1200&height=800&seq=anxian-maryceleste&orientation=landscape",
    lead:
      "1872 年 12 月，加拿大商船德·格拉蒂亚号在亚速尔群岛以东发现了一艘偏离航线的双桅帆船。它航行姿态平稳，却对任何信号都没有回应。",
    body: [
      "登船检查后，船员发现货物基本完好，船上储备了足够数月的食物和饮用水，个人物品大多留在舱内，唯独救生艇不见了。舵轮与航海仪器基本正常，没有打斗痕迹。",
      "被广泛讨论的假设包括遭遇突发风暴后短暂弃船、货物泄漏引发恐慌、以及船长担心船体进水而下令转移。这些解释各自能说明一部分现场细节，但都无法完整对应所有证据。",
      "这起案件之所以经久不衰，不在于它多么离奇，而在于它太接近日常：一艘正常运营的商船，在毫无征兆的情况下被所有人主动放弃，而放弃之后的去向再无记录。",
    ],
    timeline: [
      {
        date: "1872 年 11 月 7 日",
        title: "离开纽约",
        desc: "玛丽·塞莱斯特号载运工业酒精启航前往热那亚，船上共有十人。",
      },
      {
        date: "11 月下旬",
        title: "最后目击",
        desc: "另一艘船在亚速尔群岛以西与其短暂相遇，此后它再未被正常联络。",
      },
      {
        date: "12 月 4 日",
        title: "被发现",
        desc: "德·格拉蒂亚号发现其漂浮于海面，派员登船后确认船上空无一人。",
      },
      {
        date: "12 月 5 日",
        title: "初步检查",
        desc: "货物与给养基本完好，救生艇缺失，航海日志停记于数日之前。",
      },
      {
        date: "1873 年",
        title: "调查结束",
        desc: "海事法庭完成程序，未能确定船员下落，案件以悬案形式归档。",
      },
    ],
  },
  {
    id: "tunguska",
    title: "通古斯爆炸：西伯利亚上空的神秘冲击",
    yearLabel: "1908 年",
    year: 1908,
    era: "modern",
    category: "mystery",
    location: "俄罗斯 · 西伯利亚",
    readTime: "9 分钟",
    tags: ["空爆", "近地天体", "森林倒伏"],
    summary:
      "一次高空爆炸夷平了两千余平方公里的森林，却没有留下陨石坑，成为近地天体研究史上的经典案例。",
    cover:
      "https://readdy.ai/api/search-image?query=Vast%20Siberian%20taiga%20forest%20flattened%20outward%20in%20a%20radial%20pattern%2C%20dim%20stormy%20sky%20with%20a%20distant%20glow%20on%20the%20horizon%2C%20mist%20over%20fallen%20timber%2C%20desolate%20and%20ominous%20mood%2C%20muted%20green%20and%20charcoal%20palette%2C%20cinematic%20documentary&width=1200&height=800&seq=anxian-tunguska&orientation=landscape",
    lead:
      "1908 年 6 月 30 日清晨，西伯利亚中部的居民看到一颗极亮的火球划过天空，随后是强烈的冲击波和持续的轰鸣声。数百公里外的窗户被震碎。",
    body: [
      "由于当地地处偏远、政局动荡，第一次系统性科学考察在近二十年后才抵达。考察队发现爆炸中心附近的树木被高温烧焦却仍直立，而外围的森林则呈放射状向外倒伏。",
      "没有陨石坑这一点长期困扰研究者。目前接受度最高的解释是：一个小行星或彗星碎片在距地面数公里的高空发生剧烈空爆，能量以冲击波形式释放，天体物质大部分被气化。",
      "通古斯事件直接推动了对近地天体威胁的持续监测。今天全球范围的天体巡天项目，其问题意识很大程度上可以追溯到这次没有留下坑的爆炸。",
    ],
    timeline: [
      {
        date: "1908 年 6 月 30 日",
        title: "火球出现",
        desc: "明亮火球自东南向西北划过头顶，随后发生剧烈空爆，冲击波扩散数百公里。",
      },
      {
        date: "1908 年夏",
        title: "当地记录",
        desc: "通古斯河沿岸居民描述强光、热浪与轰鸣，部分人被冲击波掀倒。",
      },
      {
        date: "1927 年",
        title: "首次科学考察",
        desc: "库利克率队进入爆心区域，记录到放射状倒伏森林与直立焦木。",
      },
      {
        date: "1930 至 1960 年代",
        title: "空爆理论成形",
        desc: "多轮考察未发现陨石坑，高空空爆解释逐步成为主流。",
      },
      {
        date: "21 世纪",
        title: "监测体系建立",
        desc: "近地天体巡天项目普及，通古斯成为行星防御讨论的标准参照。",
      },
    ],
  },
  {
    id: "titanic",
    title: "泰坦尼克号：冰海四小时",
    yearLabel: "1912 年",
    year: 1912,
    era: "modern",
    category: "tech",
    location: "北大西洋",
    readTime: "10 分钟",
    tags: ["航海安全", "海难", "冰山"],
    summary:
      "被誉为“永不沉没”的邮轮在处女航中撞上冰山，两小时四十分钟后沉没，成为航海安全史的分水岭。",
    cover:
      "https://readdy.ai/api/search-image?query=Large%20early%20twentieth%20century%20ocean%20liner%20silhouette%20tilting%20in%20dark%20icy%20water%20at%20night%2C%20distant%20iceberg%2C%20cold%20dim%20moonlight%20through%20mist%2C%20tragic%20atmosphere%2C%20muted%20navy%20charcoal%20and%20pale%20blue%20excluding%20bright%20saturation%2C%20cinematic%20wide%20shot&width=1200&height=800&seq=anxian-titanic&orientation=landscape",
    lead:
      "1912 年 4 月，泰坦尼克号从南安普敦出发驶向纽约。它吨位巨大、舱室豪华，并通过水密舱设计被宣传为极难沉没，这种宣传强化了船员对风险的判断偏差。",
    body: [
      "撞击发生在夜间，冰山擦过船体右侧，多个水密舱被撕开。设计与损管逻辑要求相邻舱室同时进水不超过四间，而实际进水舱室数量超出了这一边界，浮力平衡无法维持。",
      "救生艇数量按照当时的法规配备，足以满足旧标准船只的吨位要求，但远不足以容纳全部乘客与船员。更重要的是，疏散程序生疏、部分救生艇未满载离船，进一步放大了伤亡。",
      "事故之后，海上人命安全公约逐步成形，包括救生设备必须匹配全员、无线电值守全天候、以及北大西洋冰情巡逻制度的建立。这些规则至今仍是国际航运安全的基础框架。",
    ],
    timeline: [
      {
        date: "1912 年 4 月 10 日",
        title: "处女航启程",
        desc: "泰坦尼克号自南安普敦出发，途经瑟堡与皇后镇后驶入大西洋。",
      },
      {
        date: "4 月 14 日 23:40",
        title: "撞上冰山",
        desc: "瞭望员发现冰山时距离过近，船体被擦伤，多间水密舱开始进水。",
      },
      {
        date: "4 月 15 日 00:15",
        title: "开始疏散",
        desc: "船员启用救生艇，但流程混乱，前几艘艇远未载满即被放下。",
      },
      {
        date: "4 月 15 日 02:20",
        title: "船体解体沉没",
        desc: "船体从中间折断后沉入海底，约一千五百人罹难，数百人被邻近船只救起。",
      },
      {
        date: "1914 年",
        title: "安全公约出台",
        desc: "海上人命安全国际公约生效，冰情巡逻与无线电值守成为强制要求。",
      },
    ],
  },
  {
    id: "spanish-flu",
    title: "西班牙流感：被战争掩盖的瘟疫",
    yearLabel: "1918 年",
    year: 1918,
    era: "modern",
    category: "disaster",
    location: "全球",
    readTime: "10 分钟",
    tags: ["大流行", "公共卫生", "1918"],
    summary:
      "在第一次世界大战的尾声，一场流感分三波席卷全球，感染人数以亿计，却被同时代的记忆长期忽略。",
    cover:
      "https://readdy.ai/api/search-image?query=Empty%20early%20twentieth%20century%20city%20street%20with%20belted%20trenchcoats%20hanging%20on%20a%20line%20and%20shuttered%20shopfronts%2C%20grey%20overcast%20light%2C%20no%20people%20visible%2C%20sombre%20restrained%20mood%2C%20muted%20sepia%20and%20ash%20tones%2C%20cinematic%20period%20photography%20feel&width=1200&height=800&seq=anxian-spanishflu&orientation=landscape",
    lead:
      "1918 年春季出现的第一波疫情相对温和，但同年秋季的第二波在传播速度与致死率上明显恶化，年轻成年人成为死亡率异常偏高的群体。",
    body: [
      "战时人员大规模集结与调动为病毒传播提供了理想条件。各国出于战争宣传考虑普遍限制疫情报道，中立国西班牙的公开报道反而让这场疫情被冠以“西班牙流感”的名称。",
      "第二波疫情中，多个城市的医疗系统在数周内被击穿。公共卫生部门被迫采取关闭学校、剧院与宗教场所，强制佩戴口罩，错峰上班等非药物干预，这些措施构成了现代大流行应对的基本范式。",
      "第三波出现在 1919 年初，随后疫情逐渐平息。由于战时审查与统计能力限制，全球死亡人数只能给出区间估计，但它至今仍是人类历史上致死人数最多的传染病事件之一。",
    ],
    timeline: [
      {
        date: "1918 年春",
        title: "第一波",
        desc: "疫情在美国军营与欧洲前线出现，传播广但致死率相对温和。",
      },
      {
        date: "1918 年秋",
        title: "第二波恶化",
        desc: "病毒传播速度与重症比例显著上升，多国城市医疗系统超负荷。",
      },
      {
        date: "1918 年 10 月",
        title: "干预措施铺开",
        desc: "关闭公共场所、强制口罩与错峰通勤等非药物干预在多个城市并行实施。",
      },
      {
        date: "1919 年初",
        title: "第三波",
        desc: "疫情在部分地区再度回升后逐步衰减，社区免疫水平上升。",
      },
      {
        date: "1920 年",
        title: "大流行结束",
        desc: "病例数降至季节性水平，但人口结构的损失已深刻影响战后社会。",
      },
    ],
  },
  {
    id: "great-depression",
    title: "黑色星期四：1929 年的崩盘",
    yearLabel: "1929 年",
    year: 1929,
    era: "modern",
    category: "disaster",
    location: "美国 · 纽约",
    readTime: "9 分钟",
    tags: ["经济史", "金融危机", "大萧条"],
    summary:
      "华尔街股市的暴跌引发连锁反应，把世界经济拖入长达十年的大萧条。",
    cover:
      "https://readdy.ai/api/search-image?query=Crowded%201920s%20New%20York%20financial%20district%20street%20in%20heavy%20shadow%2C%20tall%20stone%20buildings%20and%20ticker%20tape%20scraps%20on%20the%20ground%2C%20overcast%20tense%20mood%2C%20desaturated%20bronze%20and%20charcoal%20palette%2C%20cinematic%20documentary%20photography&width=1200&height=800&seq=anxian-depression&orientation=landscape",
    lead:
      "整个二十年代，美国股市在信贷扩张与乐观情绪推动下持续上行，大量投资者以保证金方式入市，杠杆水平被不断推高。",
    body: [
      "1929 年 10 月，股价在高位剧烈波动后开始失速。10 月 24 日的恐慌性抛售与随后的连续下跌，让杠杆头寸被迫平仓，进一步加速了下跌，形成自我强化的循环。",
      "崩盘本身并不等于大萧条，真正带来长期萧条的是随后的传导链条：银行因坏账与挤兑大面积倒闭，信贷收缩让企业无法运转，产出与就业同步下滑，价格进入持续通缩。",
      "政策应对在最初几年偏向紧缩，直到财政与货币手段转向、并建立存款保险与金融监管框架后，经济才逐步恢复。这段经历深刻塑造了此后金融监管与危机干预的基本思路。",
    ],
    timeline: [
      {
        date: "1929 年 10 月 24 日",
        title: "恐慌性抛售",
        desc: "开盘后大量卖单涌出，交易量创下纪录，银行团进场托市暂时稳住盘面。",
      },
      {
        date: "10 月 28 至 29 日",
        title: "连续暴跌",
        desc: "两个交易日累计跌幅惊人，保证金账户被大规模强制平仓。",
      },
      {
        date: "1930 至 1932 年",
        title: "银行倒闭潮",
        desc: "信贷收缩与挤兑交织，数千家银行倒闭，产出与就业持续下滑。",
      },
      {
        date: "1933 年",
        title: "政策转向",
        desc: "金融监管与存款保险制度建立，货币与财政政策开始主动干预。",
      },
      {
        date: "1939 年",
        title: "走向恢复",
        desc: "在政策调整与战时需求共同作用下，经济逐步走出萧条区间。",
      },
    ],
  },
  {
    id: "apollo-13",
    title: "阿波罗 13 号：一次成功的失败",
    yearLabel: "1970 年",
    year: 1970,
    era: "modern",
    category: "tech",
    location: "地月之间",
    readTime: "8 分钟",
    tags: ["航天", "应急处置", "系统工程"],
    summary:
      "氧气罐爆炸让登月任务变成求生行动，地面与船员的四天协作成为航天史上最经典的应急处置案例。",
    cover:
      "https://readdy.ai/api/search-image?query=Damaged%20spacecraft%20service%20module%20floating%20in%20deep%20black%20space%20with%20distant%20moon%20and%20tiny%20earth%2C%20cold%20blue%20rim%20light%20along%20the%20hull%2C%20vast%20emptiness%2C%20tense%20austere%20mood%2C%20muted%20slate%20and%20graphite%20palette%2C%20cinematic%20space%20documentary&width=1200&height=800&seq=anxian-apollo13&orientation=landscape",
    lead:
      "1970 年 4 月，阿波罗 13 号在飞往月球的途中发生氧气罐爆炸。飞船失去主电源与主要氧气供应，登月计划被立即放弃，任务目标转为把三名航天员安全带回地球。",
    body: [
      "爆炸后，指令舱迅速失能，航天员转移至登月舱，把它当作救生艇使用。登月舱原本只为两人短期月面活动设计，生命维持系统必须在超负荷条件下运转数日。",
      "地面控制团队放弃了直接掉头的方案，改用绕月后借引力返回的自由返回轨道，以节省燃料并缩短飞行时间。二氧化碳吸附装置不匹配的问题，被工程师用胶带与塑料件拼出的适配器解决。",
      "四天后，指令舱在太平洋溅落。事故调查推动了航天器冗余设计、氧气罐结构与任务中止预案的全面改进，也确立了“故障不能隐藏”的工程文化。",
    ],
    timeline: [
      {
        date: "1970 年 4 月 11 日",
        title: "发射升空",
        desc: "阿波罗 13 号按计划发射，最初数日飞行正常。",
      },
      {
        date: "4 月 13 日 21:08",
        title: "氧气罐爆炸",
        desc: "二号氧气罐爆炸，摧毁服务舱关键系统，指令舱供电与供氧中断。",
      },
      {
        date: "4 月 14 日",
        title: "启用救生艇",
        desc: "乘组转入登月舱维持生命，任务控制中心确认采用自由返回轨道。",
      },
      {
        date: "4 月 15 日",
        title: "滤芯改装修复",
        desc: "地面指导乘组用现有材料拼接适配器，解决二氧化碳浓度超标问题。",
      },
      {
        date: "4 月 17 日",
        title: "安全溅落",
        desc: "指令舱在太平洋海域溅落，三名航天员全部生还。",
      },
    ],
  },
  {
    id: "chernobyl",
    title: "切尔诺贝利：核灾的七十二小时",
    yearLabel: "1986 年",
    year: 1986,
    era: "modern",
    category: "tech",
    location: "乌克兰 · 普里皮亚季",
    readTime: "12 分钟",
    featured: true,
    tags: ["核安全", "应急响应", "辐射"],
    summary:
      "一次测试中的功率失控导致反应堆损毁，放射性尘埃扩散至欧洲多国，也从根本上改变了世界核能政策的走向。",
    cover:
      "https://readdy.ai/api/search-image?query=Abandoned%20concrete%20industrial%20complex%20at%20night%20under%20a%20dark%20sky%2C%20empty%20paved%20plaza%20and%20rusting%20structures%2C%20single%20searchlight%20beam%2C%20desolate%20chilling%20atmosphere%2C%20muted%20grey%20and%20faded%20teal%20palette%2C%20cinematic%20long%20exposure&width=1200&height=800&seq=anxian-chernobyl&orientation=landscape",
    lead:
      "1986 年 4 月 26 日凌晨，四号反应堆在一次按计划进行的惰转测试中功率急剧上升，随后发生蒸汽爆炸并引发石墨燃烧，大量放射性物质被抛入大气。",
    body: [
      "反应堆的安全特性在这一类型设计中存在固有缺陷：在特定工况下，控制棒插入的瞬间会短暂增加反应性，形成所谓的正反应性插入效应。测试流程与操作规程的叠加失误，最终触发了这一机制。",
      "事故后的处置分为若干阶段：首先是现场灭火与控制火势，随后是疏散周边居民，再之后是长期的封堵与“石棺”建造。参与初期处置的人员承受了极高剂量的辐射。",
      "事故的长期影响同时体现在健康、环境与制度三个层面。周边地区的甲状腺癌发病率显著上升，大片森林与农田被长期限制使用，而全球核能的安全监管标准与信息公开要求也随之被系统性重写。",
    ],
    timeline: [
      {
        date: "1986 年 4 月 26 日 01:23",
        title: "功率失控",
        desc: "测试过程中反应堆功率异常上升，操作员按下紧急停堆按钮后发生蒸汽爆炸。",
      },
      {
        date: "4 月 26 日凌晨",
        title: "初期处置",
        desc: "值班人员与消防队进入现场控制火势，部分人员很快出现急性辐射症状。",
      },
      {
        date: "4 月 27 日",
        title: "紧急疏散",
        desc: "普里皮亚季及周边居民开始大规模撤离，疏散范围随后持续扩大。",
      },
      {
        date: "1986 年 5 至 11 月",
        title: "封堵与石棺",
        desc: "大量人员参与清理与结构封堵，混凝土石棺在事故当年内完成主体建造。",
      },
      {
        date: "2016 年",
        title: "新安全壳启用",
        desc: "移动式拱形安全壳完成滑移就位，用于长期屏蔽与后续拆除作业。",
      },
    ],
  },
  {
    id: "fukushima",
    title: "福岛核事故：海啸之后",
    yearLabel: "2011 年",
    year: 2011,
    era: "contemporary",
    category: "tech",
    location: "日本 · 福岛县",
    readTime: "11 分钟",
    tags: ["核安全", "地震海啸", "应急电源"],
    summary:
      "大地震引发的海啸淹没了应急电源，导致反应堆无法有效冷却，这场事故让核安全标准被彻底重新审视。",
    cover:
      "https://readdy.ai/api/search-image?query=Coastal%20industrial%20power%20plant%20seen%20from%20a%20distance%20across%20a%20grey%20stormy%20sea%2C%20damaged%20structures%20and%20debris%20along%20the%20shore%2C%20overcast%20sombre%20sky%2C%20muted%20cold%20grey%20and%20dull%20green%20palette%2C%20cinematic%20restrained%20documentary%20tone&width=1200&height=800&seq=anxian-fukushima&orientation=landscape",
    lead:
      "2011 年 3 月 11 日，日本东北近海发生强烈地震。核电站按设计成功自动停堆，但随后抵达的海啸高度超出了防波堤的设防标准。",
    body: [
      "海啸淹没了位于地下的应急柴油发电机与配电设备，全厂断电导致冷却系统失去动力。堆芯余热无法排出，燃料温度持续升高，随后发生堆芯熔毁与氢气爆炸，造成放射性物质向外释放。",
      "事故暴露出的是系统性认知偏差：设计基准把极端海啸的评估建立在有限的历史记录上，同时应急电源的布置位置与冗余方式未能覆盖被水淹的复合情景。",
      "事故之后，多国监管机构重新审视了极端外部事件叠加的评估方法，要求提升应急电源的防水与分散布置、增加移动式应急设备，并强化严重事故的场外应急预案。",
    ],
    timeline: [
      {
        date: "2011 年 3 月 11 日 14:46",
        title: "强震发生",
        desc: "近海发生特大地震，多座反应堆自动停堆，外部电网中断。",
      },
      {
        date: "当日 15:41",
        title: "海啸越堤",
        desc: "海啸抵达并越过防波堤，淹没应急发电机与配电设施，全厂断电。",
      },
      {
        date: "3 月 12 日",
        title: "氢气爆炸",
        desc: "一号机组厂房发生氢气爆炸，随后多台机组相继出现类似情形。",
      },
      {
        date: "3 月 12 至 15 日",
        title: "疏散扩大",
        desc: "周边居民疏散范围逐步扩大，多项应急监测与供水作业同步展开。",
      },
      {
        date: "2011 年 12 月",
        title: "冷停堆确认",
        desc: "反应堆进入稳定冷却状态，后续的去污与退役工作长期持续。",
      },
    ],
  },
  {
    id: "mh370",
    title: "马航 MH370：消失的航班",
    yearLabel: "2014 年",
    year: 2014,
    era: "contemporary",
    category: "mystery",
    location: "印度洋",
    readTime: "10 分钟",
    summary:
      "一架载有二百余人的客机在雷达上消失，随后多年的大规模搜寻只找到零星残骸，成为航空史上最大的谜团之一。",
    cover:
      "https://readdy.ai/api/search-image?query=Commercial%20airliner%20silhouette%20crossing%20a%20dark%20stormy%20ocean%20horizon%20at%20dusk%2C%20heavy%20clouds%20and%20dim%20band%20of%20light%2C%20vast%20empty%20sea%20below%2C%20enigmatic%20sombre%20mood%2C%20muted%20slate%20and%20pale%20amber%20palette%2C%20cinematic%20aviation%20documentary&width=1200&height=800&seq=anxian-mh370&orientation=landscape",
    tags: ["航空", "搜寻", "卫星数据"],
    lead:
      "2014 年 3 月 8 日凌晨，这架客机从吉隆坡起飞前往北京，在进入越南空管区域前后与地面失去常规无线电联系，但军用雷达仍记录到它转向飞越马来半岛。",
    body: [
      "随后被公开的卫星通信数据表明，飞机在失联后又持续飞行了数小时。依据与卫星之间的握手信号和多普勒频移分析，调查方推断其最终向南飞入南印度洋，但具体的飞行路径存在较大不确定性。",
      "由多国参与的搜救行动先在南中国海与马六甲海峡展开，随后转向南印度洋。水下搜寻覆盖了大片高优先区域，但未能定位主残骸。此后的数年里，非洲东海岸与印度洋岛屿陆续发现少量被确认属于该机的部件。",
      "事故的持续未解推动了航空领域的多项改进，包括客机位置报告间隔缩短、飞行数据流的实时传输要求，以及水下定位信标电池寿命的延长。",
    ],
    timeline: [
      {
        date: "2014 年 3 月 8 日 00:41",
        title: "起飞",
        desc: "航班自吉隆坡起飞前往北京，机上共有二百余名乘客与机组。",
      },
      {
        date: "当日 01:19 前后",
        title: "最后通话",
        desc: "机组与空管完成最后通话，此后常规通信与应答机信号中断。",
      },
      {
        date: "3 月 8 日",
        title: "军用雷达记录",
        desc: "雷达数据显示飞机转向西飞越马来半岛后转往安达曼海方向。",
      },
      {
        date: "3 月 15 日",
        title: "搜寻转向",
        desc: "卫星数据被公开后，搜救重心由南中国海转向南印度洋。",
      },
      {
        date: "2015 年起",
        title: "残骸发现",
        desc: "留尼汪岛等地陆续发现被确认的部件，主残骸位置仍未被确定。",
      },
    ],
  },
];

export const faqs: ArchiveFaq[] = [
  {
    q: "暗线档案的内容是怎么组织的？",
    a: "每一条档案都按照事件真实发生的时间顺序展开：先给出背景与关键背景信息，再用时间轴把关键节点逐条排开。我们希望读者读完一条档案后，能在脑海里形成一条完整的时间线，而不是零散的事实片段。",
  },
  {
    q: "为什么有些事件没有给出确定结论？",
    a: "未解之谜栏目里的条目，往往存在多个互相竞争的解释。我们的做法是把已确认的证据与仍然开放的疑问分开呈现，标注哪些是记录、哪些是推断，而不是替读者下结论。",
  },
  {
    q: "时间轴上的年代是怎么标注的？",
    a: "古代条目通常只能确定到世纪或大致年份，我们会用“约”字标注不确定性；近代以后的事件尽量精确到日，并在关键节点标注具体时刻。年代跨度大的条目会分段说明，避免把几百年的过程压缩成一个点。",
  },
  {
    q: "网站内容适合作为研究引用吗？",
    a: "网站内容更适合作为理解事件脉络的入门读物和线索索引。涉及具体数据、伤亡统计或法律结论时，建议进一步查阅官方调查报告与学术论文，并核对原始出处。",
  },
  {
    q: "为什么不同条目的详细程度不一样？",
    a: "取决于公开资料的密度。有的主题有完整的事故调查报告和大量学术研究，可以写得非常细；有的主题只有零散记载，我们只能保留已有的信息量，不为凑篇幅而补充未经证实的细节。",
  },
  {
    q: "怎样持续跟进新上线的档案？",
    a: "可以在页面底部订阅更新，我们会在每个专题整理完成后发送一次汇总，内容包括新增档案、时间轴修正和读者指出的错误更正。单个条目一般每季度复核一次。",
  },
];

export const voices: ArchiveVoice[] = [
  {
    name: "陆之遥",
    role: "历史教师",
    text: "备课时翻过很多资料，最常见的问题是时间线混乱。这里的条目会把关键节点按顺序排好，我直接拿来讲给学生听，节省了大量核对顺序的时间。",
  },
  {
    name: "周与岸",
    role: "纪录片撰稿",
    text: "写脚本前我会先来看一遍时间轴，它能帮我把叙述节奏铺开。尤其是科技事故那几篇，事故链的因果关系梳理得很清楚，不夸张也不省略。",
  },
  {
    name: "何见微",
    role: "长期读者",
    text: "最欣赏的是它会明确区分“已经确认”和“仍在推测”。看悬案最怕被带着走，这里会告诉你证据到哪里为止。",
  },
];

export const stats = [
  { label: "已整理档案", value: "214", unit: "条" },
  { label: "覆盖纪元", value: "5", unit: "个" },
  { label: "时间轴节点", value: "1,860", unit: "个" },
  { label: "每季度复核", value: "100", unit: "%" },
];